import { Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { v4 as uuid } from 'uuid';
import { OAUTH_CLIENT_ID, OAUTH_URL } from '@kikkz/b04/auth/util';
import { LOGGING_SERVICE } from '@kikkz/shared/logging/util';
import { IB04AuthService, OAuthConfig, OAuthTokenResponse } from '@kikkz/b04/auth/util';
import { generatePkceChallenge, generatePkceVerifier } from '../helper/pkce-utils';
import { AuthStorageService } from './auth-storage.service';

/**
 * B04 tenant auth service. Implements the Bayer 04 OAuth2 (Authorization Code + PKCE) flow.
 */
@Injectable({
  providedIn: 'root',
})
export class B04AuthService implements IB04AuthService {
  private readonly AUTHORIZE_URL_BASE = inject(OAUTH_URL);
  private readonly AUTHORIZE_PATH = 'authorize';
  private readonly TOKEN_PATH = 'token';
  private readonly OAUTH_CLIENT_ID = inject(OAUTH_CLIENT_ID);
  private readonly document = inject(DOCUMENT);
  private readonly authStorageService = inject(AuthStorageService);
  private readonly httpClient = inject(HttpClient);
  private readonly loggingService = inject(LOGGING_SERVICE);

  private readonly CODE_CHALLENGE_METHOD_VALUE = 'S256';
  private readonly GRANT_TYPE_VALUE = 'authorization_code';
  private readonly SCOPES_VALUE = 'openid entertainment';
  private readonly REDIRECT_URI = this.document.location.origin + '/auth/callback';

  private readonly defaultConfig: Partial<OAuthConfig> = {
    client_id: this.OAUTH_CLIENT_ID,
    redirect_uri: this.REDIRECT_URI,
    scope: this.SCOPES_VALUE,
    response_type: 'code',
  };

  async initiateOAuthFlow(config?: Partial<OAuthConfig>): Promise<void> {
    const state = uuid();
    const codeVerifier = generatePkceVerifier();
    const codeChallenge = await generatePkceChallenge(codeVerifier);

    const finalConfig = {
      ...this.defaultConfig,
      ...config,
      state,
      code_challenge_method: this.CODE_CHALLENGE_METHOD_VALUE,
      code_challenge: codeChallenge,
    };

    this.authStorageService.saveCodeVerifier(codeVerifier);
    this.authStorageService.saveAuthState(state);

    const params = new URLSearchParams(finalConfig);

    const authUrl = `${this.AUTHORIZE_URL_BASE}/${this.AUTHORIZE_PATH}?${params.toString()}`;

    window.location.href = authUrl;
  }

  exchangeCodeForToken$(code: string, state: string): Observable<OAuthTokenResponse> {
    const codeVerifier = this.authStorageService.getCodeVerifier();
    const savedState = this.authStorageService.getAuthState();

    if (!codeVerifier) {
      this.loggingService.error('Missing PKCE code_verifier');
      throw new Error('Missing PKCE code_verifier');
    }

    if (!savedState) {
      this.loggingService.error('Missing state parameter');
      throw new Error('Missing state parameter');
    }

    if (savedState !== state) {
      this.loggingService.error('Invalid state parameter');
      throw new Error('Invalid state parameter');
    }

    const body = new HttpParams()
      .set('grant_type', this.GRANT_TYPE_VALUE)
      .set('redirect_uri', this.REDIRECT_URI)
      .set('code_verifier', codeVerifier)
      .set('code', code)
      .set('client_id', this.OAUTH_CLIENT_ID);

    const url = `${this.AUTHORIZE_URL_BASE}/${this.TOKEN_PATH}/`;

    return this.httpClient.post<OAuthTokenResponse>(url, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  refreshToken$(refreshToken: string): Observable<OAuthTokenResponse> {
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('refresh_token', refreshToken)
      .set('redirect_uri', this.REDIRECT_URI)
      .set('client_id', this.OAUTH_CLIENT_ID);

    const url = `${this.AUTHORIZE_URL_BASE}/${this.TOKEN_PATH}/`;

    return this.httpClient.post<OAuthTokenResponse>(url, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  logout(): void {
    this.authStorageService.clearAuthTokens();
    this.authStorageService.clearSessionVariables();
  }
}
